import { apiGet, apiPost } from '../../../adapters/api-client/api';
import { MyCourse } from './courses';

/**
 * The course review gate's operator endpoints — UC-A08.
 *
 * Every call is authorised server-side (AC-21): reading needs the queue
 * permission and deciding needs the authority over a teacher's live listings.
 */

export type CourseReviewDecision = 'APPROVED' | 'CHANGES_REQUIRED' | 'REJECTED';

export interface ReviewSla {
  state: 'BREACHED' | 'DUE_SOON' | 'SCHEDULED';
  dueAt: string;
  hoursRemaining: number;
  /** The governed due-soon window this was judged with — DEC-A02-07. */
  dueSoonHours: number;
  /** Past the governed target. A label for a person, never a decision (BR-A08-07). */
  overdue: boolean;
}

/** One submission in the queue — ADM-08-FR-02. */
export interface CourseReviewQueueItem {
  courseId: string;
  title: string;
  teacherId: string;
  subject: string;
  /** The exact version a decision will bind to. */
  courseVersionId: string;
  version: number;
  submittedAt: string;
  resubmissionCount: number;
  slaHours: number;
  sla: ReviewSla;
}

export interface CourseReviewRecord {
  courseId: string;
  courseVersionId: string;
  version: number;
  decision: CourseReviewDecision;
  feedback?: string;
  reviewerId: string;
  decidedAt: string;
}

/** What the reviewer opens: the frozen version, never the working copy (AC-01). */
export interface CourseSubmissionView {
  listing: MyCourse;
  version: {
    courseId: string;
    courseVersionId: string;
    version: number;
    submittedAt?: string;
    content: Record<string, unknown>;
  };
  decisions: CourseReviewRecord[];
  maxResubmissions: number;
  resubmissionsRemaining: number;
  slaHours: number;
  sla: ReviewSla | null;
}

export interface CourseReviewOutcome {
  decision: CourseReviewRecord;
  listing: MyCourse;
  replayed: boolean;
  /** For an approval: whether publication ran, and if not, why (AD-A08-05). */
  publication: { published: boolean; blockedBy?: string } | null;
}

export function listCourseReviews(): Promise<{ submissions: CourseReviewQueueItem[] }> {
  return apiGet('/api/v1/admin/course-reviews');
}

export function getCourseSubmission(courseId: string): Promise<CourseSubmissionView> {
  return apiGet(`/api/v1/admin/course-reviews/${courseId}`);
}

/** Bound to the version on screen; a stale one is refused server-side (A13). */
export function decideCourseReview(
  courseId: string,
  body: { courseVersionId: string; decision: CourseReviewDecision; feedback?: string },
): Promise<CourseReviewOutcome> {
  return apiPost(`/api/v1/admin/course-reviews/${courseId}/decision`, body);
}
